<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import DescriptionList from '@leaflink/stash/DescriptionList.vue';
  import DescriptionListDetail from '@leaflink/stash/DescriptionListDetail.vue';
  import DescriptionListGroup from '@leaflink/stash/DescriptionListGroup.vue';
  import DescriptionListTerm from '@leaflink/stash/DescriptionListTerm.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import { formatISO } from 'date-fns';
  import { computed, ref } from 'vue';

  import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
  import { createSession } from '@/services/session';
  import { isHttpError } from '@/utils/errors';

  const isOpen = ref(false);

  const appConfigStore = useAppConfigStore();

  const environment = computed(() => {
    if (window.location.hostname === 'localhost') {
      return 'LOCAL';
    }

    return String(appConfigStore.environment).toUpperCase();
  });

  const mpApiHostInput = ref(appConfigStore.mpApiHost);
  const mpApiHostErrorText = ref<string>();
  const isMpApiHostUnsaved = computed(() => mpApiHostInput.value !== appConfigStore.mpApiHost);

  /**
   * Sets or resets the marketplace API host. If the input value is different from the active value, the new value is set as active; otherwise, the active value is reset to its default.
   *
   * Valid Hosts include:
   *  - localhost:8000
   *  - app.dev.leaflink.io
   *  - leaflink-l123456.dev.leaflink.io (backend PR env)
   *
   * Invalid Hosts include:
   *  - hosts not hosted by LeafLink
   *  - production and production-like hosts
   */
  async function onMpApiHostInputSubmit() {
    const host = isMpApiHostUnsaved.value ? mpApiHostInput.value : appConfigStore.defaultMpApiHost;

    if (host !== appConfigStore.defaultMpApiHost) {
      mpApiHostErrorText.value = undefined;

      try {
        const httpSession = createSession(window.location.protocol + '//' + host, { shouldTransform: false });
        await httpSession.get('', { withCredentials: true });
      } catch (error) {
        if (isHttpError(error) && !error.response) {
          mpApiHostErrorText.value = 'Invalid API host';
          return;
        } else {
          throw error;
        }
      }
    }

    appConfigStore.setMpApiHost({ host, reloadWindow: true });
  }

  const unifiedApiUrlInput = ref(appConfigStore.unifiedApiUrl);
  const unifiedApiUrlErrorText = ref<string>();
  const isUnifiedApiUrlUnsaved = computed(() => unifiedApiUrlInput.value !== appConfigStore.unifiedApiUrl);

  /**
   * Sets or resets the Unified API URL. If the input value is different from the active value, the new value is set as active; otherwise, the active value is reset to its default.
   *
   * Valid URLs include:
   *  - http://localhost:8000
   *  - https://api.dev.leaflink.io
   *  - https://leaflink-l123456.dev.leaflink.io (backend PR env)
   *
   * Invalid URLs include:
   *  - hosts not hosted by LeafLink
   *  - production and production-like hosts
   */
  async function onUnifedApiUrlInputSubmit() {
    const url = isUnifiedApiUrlUnsaved.value ? unifiedApiUrlInput.value : appConfigStore.defaultUnifiedApiUrl;

    if (url !== appConfigStore.defaultUnifiedApiUrl) {
      unifiedApiUrlErrorText.value = undefined;

      try {
        const httpSession = createSession(url, { shouldTransform: false });
        await httpSession.get('', { withCredentials: true });
      } catch (error) {
        if (isHttpError(error) && !error.response) {
          unifiedApiUrlErrorText.value = 'Invalid API URL';
          return;
        } else {
          throw error;
        }
      }
    }

    appConfigStore.setUnifiedApiUrl({ url, reloadWindow: true });
  }
</script>

<template>
  <div data-test="env-drawer" :class="[$style['environment-drawer'], 'tw-w-full tw-bg-purple-500 tw-z-content']">
    <div
      :class="[
        $style.header,
        'tw-cursor-pointer heading-4 tw-rounded-t tw-p-3 tw-bg-orange-500 tw-flex tw-justify-between tw-items-center tw-text-ice-900',
      ]"
      @click="isOpen = !isOpen"
    >
      <span :class="$style.env">
        <strong>Environment: {{ environment }}</strong>
      </span>
      <span>
        <Icon name="caret-down" :class="[isOpen ? '' : 'rotate-180', 'color-ice-900']" />
      </span>
    </div>

    <section data-test="env-drawer|content" :class="[isOpen ? 'tw-block' : 'tw-hidden', 'tw-p-2']">
      <div class="tw-ice-ice tw-text-xs">
        <form
          class="tw-flex tw-gap-x-1 tw-items-start tw-pb-3 !tw-text-xs"
          data-test="form|mp-api-host"
          @submit.prevent="onMpApiHostInputSubmit"
        >
          <DescriptionList v-if="environment === 'LOCAL'" :class="[$style.localApiInfo]" variant="stacked">
            <DescriptionListGroup>
              <DescriptionListTerm>MP API Host</DescriptionListTerm>
              <DescriptionListDetail data-test="env-drawer|mp-api-host-value">{{
                appConfigStore.mpApiHost
              }}</DescriptionListDetail>
            </DescriptionListGroup>
          </DescriptionList>
          <Input
            v-else
            v-model="mpApiHostInput"
            label="MP API Host"
            :class="['tw-w-full', $style.input]"
            :error-text="mpApiHostErrorText"
          >
            <template v-if="isMpApiHostUnsaved || appConfigStore.isMpApiHostCustom" #append>
              <Button
                data-test="mp-api-host-submit-button"
                :title="isMpApiHostUnsaved ? 'Save' : 'Reset'"
                type="submit"
              >
                <Icon :name="isMpApiHostUnsaved ? 'save' : 'close'" />
              </Button>
            </template>
          </Input>
        </form>
        <form
          class="tw-flex tw-gap-x-1 tw-items-start tw-pb-3 !tw-text-xs"
          data-test="form|unified-api-url"
          @submit.prevent="onUnifedApiUrlInputSubmit"
        >
          <Input
            v-model="unifiedApiUrlInput"
            label="Unified API URL"
            :class="['tw-w-full', $style.input]"
            :error-text="unifiedApiUrlErrorText"
          >
            <template v-if="isUnifiedApiUrlUnsaved || appConfigStore.isUnifiedApiUrlCustom" #append>
              <Button
                data-test="unified-api-url-submit-button"
                :title="isUnifiedApiUrlUnsaved ? 'Save' : 'Reset'"
                type="submit"
              >
                <Icon :name="isUnifiedApiUrlUnsaved ? 'save' : 'close'" />
              </Button>
            </template>
          </Input>
        </form>
      </div>

      <DescriptionList :class="['tw-pt-2', $style.buildInfo]" variant="stacked">
        <DescriptionListGroup>
          <DescriptionListTerm>Build date (ISO)</DescriptionListTerm>
          <DescriptionListDetail
            v-if="appConfigStore.buildInfo?.buildDate"
            data-test="env-drawer|build-date"
            class="show-empty"
          >
            {{ formatISO(appConfigStore.buildInfo.buildDate * 1000) }}
          </DescriptionListDetail>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Git hash</DescriptionListTerm>
          <DescriptionListDetail data-test="env-drawer|git-hash" class="show-empty">
            {{ appConfigStore.buildInfo?.gitHash }}
          </DescriptionListDetail>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Python</DescriptionListTerm>
          <DescriptionListDetail data-test="env-drawer|python-version" class="show-empty">
            {{ appConfigStore.buildInfo?.pythonVersion }}
          </DescriptionListDetail>
        </DescriptionListGroup>
        <DescriptionListGroup class="tw-mb-0">
          <DescriptionListTerm>Django</DescriptionListTerm>
          <DescriptionListDetail data-test="env-drawer|django-version" class="show-empty">
            {{ appConfigStore.buildInfo?.djangoVersion }}
          </DescriptionListDetail>
        </DescriptionListGroup>
      </DescriptionList>
    </section>
  </div>
</template>

<style module>
  .environment-drawer {
    bottom: 0;
    position: absolute;
  }

  .header {
    height: 25px;
  }

  .content {
    min-height: 100px;
  }

  .input :global(.stash-field-error) {
    @apply tw-text-red-400;
  }

  .input :global(.stash-label),
  .localApiInfo dt,
  .buildInfo dt {
    @apply tw-text-xs tw-font-bold tw-text-ice-500 tw-pb-0;
  }

  .localApiInfo dd,
  .buildInfo dd {
    @apply tw-text-ice-100;
  }

  .env {
    line-height: calc(20 / 14); /* typo('body', 'line-height') */
    padding-top: 4px; /* because the Sofia font adds extra bottom space but not top space */
  }
</style>
